const BASE_URL = "https://noiz.gg";
const DEFAULT_TITLE = "Noiz.gg | A New, Fun Way to Monetize";
const DEFAULT_DESCRIPTION =
  "NOIZ brings streamers and publishers together with first looks, fresh content and more.";
// Stopgap: the NOIZ brand mark (square). Replace with a dedicated 1200x630 social
// banner at a stable public URL, then switch the twitter:card back to
// "summary_large_image" in updateDocumentSeo and index.html.
const DEFAULT_IMAGE = "https://noiz.gg/android-chrome-512x512.png";

const PUBLIC_PAGE_META = [
  {
    match: /^\/$/,
    title: "Noiz.gg | A New, Fun Way to Monetize",
    description: DEFAULT_DESCRIPTION,
  },
  {
    match: /^\/stream-with-us\/?$/,
    title: "Stream With Us | Noiz.gg",
    description:
      "Join Noiz and get paid to stream games you love with campaigns built for creators.",
  },
  {
    match: /^\/about-us\/?$/,
    title: "About Us | Noiz.gg",
    description:
      "Learn about Noiz, our mission, and how we connect streamers with publishers and brands.",
  },
  {
    match: /^\/careers(\/.*)?$/,
    title: "Careers | Noiz.gg",
    description:
      "Explore career opportunities at Noiz and help shape the future of creator monetization.",
  },
  {
    match: /^\/faqs\/?$/,
    title: "FAQs | Noiz.gg",
    description:
      "Find answers to common questions about Noiz, campaigns, and creator payouts.",
  },
  {
    match: /^\/contact\/?$/,
    title: "Contact | Noiz.gg",
    description:
      "Get in touch with the Noiz team for partnership, support, or platform questions.",
  },
  {
    match: /^\/privacy-policy\/?$/,
    title: "Privacy Policy | Noiz.gg",
    description: "Read Noiz privacy policy and how we process your data.",
  },
  {
    match: /^\/terms-of-use\/?$/,
    title: "Terms of Use | Noiz.gg",
    description:
      "Review the terms of use for Noiz services and platform access.",
  },
  {
    match: /^\/terms-of-service\/?$/,
    title: "Terms of Service | Noiz.gg",
    description: "Review Noiz terms of service for all users and partners.",
  },
];

const NOINDEX_PATHS = [
  /^\/admin(\/|$)/,
  /^\/portal(\/|$)/,
  /^\/my-account(\/|$)/,
  /^\/my-quests(\/|$)/,
  /^\/payment(\/|$)/,
  /^\/withdrawal(\/|$)/,
  /^\/payout(\/|$)/,
  /^\/connect-accounts(\/|$)/,
  /^\/complete-registration(\/|$)/,
  /^\/welcome(\/|$)/,
  /^\/logout(\/|$)/,
  /^\/search\/(games|quests)(\/|$)/,
  /^\/product-management(\/|$)/,
  /^\/drops(\/|$)/,
  /^\/buy(\/|$)/,
  /^\/quests\/[A-Za-z0-9_-]+$/,
  // /quest/:slug (LandingQuest) is a public campaign landing page — intentionally
  // left indexable. NOTE: it currently falls back to the default title/description;
  // give each quest a unique title/description to avoid duplicate-title penalties.
  /^\/twitch-drop\/[A-Za-z0-9_-]+$/,
];

const upsertMetaByName = (name, content) => {
  if (!content) return;
  let tag = document.querySelector(`meta[name="${name}"]`);
  if (!tag) {
    tag = document.createElement("meta");
    tag.setAttribute("name", name);
    document.head.appendChild(tag);
  }
  tag.setAttribute("content", content);
};

const upsertMetaByProperty = (property, content) => {
  if (!content) return;
  let tag = document.querySelector(`meta[property="${property}"]`);
  if (!tag) {
    tag = document.createElement("meta");
    tag.setAttribute("property", property);
    document.head.appendChild(tag);
  }
  tag.setAttribute("content", content);
};

const upsertCanonical = (href) => {
  let link = document.querySelector('link[rel="canonical"]');
  if (!link) {
    link = document.createElement("link");
    link.setAttribute("rel", "canonical");
    document.head.appendChild(link);
  }
  link.setAttribute("href", href);
};

export const shouldNoindex = (pathname = "") =>
  NOINDEX_PATHS.some((rule) => rule.test(pathname));

export const getSeoMetaForPath = (pathname = "") => {
  const matched = PUBLIC_PAGE_META.find((page) => page.match.test(pathname));
  return {
    title: matched?.title || DEFAULT_TITLE,
    description: matched?.description || DEFAULT_DESCRIPTION,
  };
};

export const updateDocumentSeo = (location = {}) => {
  const pathname = location.pathname || window.location.pathname || "/";
  const { title, description } = getSeoMetaForPath(pathname);

  // Canonical/og:url intentionally exclude the query string. Marketing params
  // (utm_*, source, ref, ...) must not fork the canonical, or each variant becomes
  // its own "canonical" URL and duplicate-consolidation is lost.
  const canonical = `${BASE_URL}${pathname}`;
  const robots = shouldNoindex(pathname)
    ? "noindex, nofollow"
    : "index, follow";

  document.title = title;
  upsertCanonical(canonical);
  upsertMetaByName("description", description);
  upsertMetaByName("robots", robots);
  upsertMetaByName("twitter:card", "summary");
  upsertMetaByName("twitter:title", title);
  upsertMetaByName("twitter:description", description);
  upsertMetaByName("twitter:image", DEFAULT_IMAGE);

  upsertMetaByProperty("og:title", title);
  upsertMetaByProperty("og:description", description);
  upsertMetaByProperty("og:type", "website");
  upsertMetaByProperty("og:url", canonical);
  upsertMetaByProperty("og:image", DEFAULT_IMAGE);
  upsertMetaByProperty("og:site_name", "Noiz.gg");
};
